import { reactive } from 'vue';
import api from './gymTrackerApi.js';

const PREFIX = 'gymtracker_';
const lsGet = k => { try { return localStorage.getItem(PREFIX + k); } catch { return null; } };
const lsSet = (k,v) => { try { localStorage.setItem(PREFIX + k, v); } catch { /* ignore */ } };
const lsRemove = k => { try { localStorage.removeItem(PREFIX + k); } catch { /* ignore */ } };

export const gymAuthState = reactive({
  loggedIn: false,
  username: lsGet('username') || null,
  sessionId: lsGet('sessionId') || null,
  token: lsGet('token') || null,
  refreshToken: lsGet('refreshToken') || null,
  expiration: lsGet('expiration') || null,
  loading: false,
  error: null,
});

function persist() { ['username','sessionId','token','refreshToken','expiration'].forEach(k => lsSet(k, gymAuthState[k] || '')); }
function clear() { ['username','sessionId','token','refreshToken','expiration'].forEach(lsRemove); }

export async function login(username, password) {
  gymAuthState.loading = true; gymAuthState.error = null;
  try {
    const d = await api.login(username, password);
    if (!d.login?.success) throw new Error('Inloggning misslyckades');
    const r = d.login;
    gymAuthState.loggedIn = true;
    gymAuthState.username = r.userName || username;
    gymAuthState.sessionId = r.sessionId;
    gymAuthState.token = r.token;
    gymAuthState.refreshToken = r.refreshToken;
    gymAuthState.expiration = r.expiration;
    persist();
    return true;
  } catch(e) {
    console.error('[GymTrackerAuth] Login failed:', e);
    gymAuthState.error = e.message;
    gymAuthState.loggedIn = false;
    return false;
  } finally { gymAuthState.loading = false; }
}

export async function logout() {
  if (gymAuthState.sessionId) { try { await api.logout(gymAuthState.sessionId); } catch { /* ignore */ } }
  gymAuthState.loggedIn = false; gymAuthState.username = null; gymAuthState.sessionId = null;
  gymAuthState.token = null; gymAuthState.refreshToken = null; gymAuthState.expiration = null;
  clear();
}

export async function refreshIfNeeded() {
  if (!gymAuthState.token || !gymAuthState.refreshToken || !gymAuthState.sessionId) return false;
  const expMs = new Date(gymAuthState.expiration).getTime();
  if (isNaN(expMs)) return false;
  const remaining = expMs - Date.now();
  if (remaining > 2 * 60 * 1000) return true;
  try {
    const d = await api.refreshAccessToken(gymAuthState.sessionId, gymAuthState.refreshToken);
    if (d.refreshAccessToken?.success) {
      gymAuthState.token = d.refreshAccessToken.accessToken;
      gymAuthState.refreshToken = d.refreshAccessToken.newRefreshToken;
      gymAuthState.expiration = d.refreshAccessToken.accessTokenExpiration;
      persist();
      return true;
    }
    await logout();
    return false;
  } catch(e) {
    console.warn('[GymTrackerAuth] Refresh misslyckades:', e.message);
    await logout();
    return false;
  }
}

export async function restoreSession() {
  if (!gymAuthState.token || !gymAuthState.sessionId) return;
  try {
    const tokenOk = await refreshIfNeeded();
    if (!tokenOk && !gymAuthState.token) return;
    const d = await api.validateSession(gymAuthState.sessionId);
    const result = d?.validateSession;
    if (result?.isValid) {
      gymAuthState.loggedIn = true;
    } else {
      console.info('[GymTrackerAuth] Session ogiltig, loggar ut.');
      await logout();
    }
  } catch(e) {
    console.warn('[GymTrackerAuth] Kunde inte validera session:', e.message);
    // Vid nätverksfel – behåll inte loggedIn, kräv ny inloggning
    gymAuthState.loggedIn = false;
  }
}

// Auto-restore session on import (only if we have a stored token)
restoreSession();

export async function fetchMyLifts() {
  if (!gymAuthState.loggedIn) return [];
  await refreshIfNeeded();
  try {
    const d = await api.myLifts(gymAuthState.token);
    return d.myLifts || [];
  } catch(e) {
    console.error('[GymTrackerAuth] fetchMyLifts failed:', e);
    gymAuthState.error = e.message;
    return [];
  }
}
